<template class="text_google">
  <v-app>
    <!-- Enhanced System Bar -->
    <v-system-bar
      class="noprint"
      color="primary"
      dark
      height="60"
      app
      lights-out
    >
      <v-container fluid class="pa-0">
        <v-row align="center" no-gutters>
          <v-col cols="auto">
            <v-toolbar-items class="ml-2">
              <v-btn
                @click="handlePrint"
                class="elevation-0 mr-2"
                color="white"
                outlined
                large
                :loading="printing"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-printer</v-icon>
                พิมพ์รายงาน
              </v-btn>

              <v-btn
                @click="exportToWord"
                class="elevation-0 mr-2"
                color="success"
                large
                :loading="exporting"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-file-word</v-icon>
                Export Word
              </v-btn>

              <v-btn
                @click="exportToPDF"
                class="elevation-0 mr-2"
                color="error"
                large
                :loading="exportingPDF"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Export PDF
              </v-btn>

              <v-btn
                @click="exportToExcel"
                class="elevation-0"
                color="orange"
                large
                :loading="exportingExcel"
                :disabled="loading || !hasData"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Export Excel
              </v-btn>
            </v-toolbar-items>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="auto">
            <v-chip class="mr-2" :color="statusChipColor" small outlined>
              <v-icon left small>{{ statusIcon }}</v-icon>
              {{ statusText }}
            </v-chip>

            <v-btn
              @click="refreshData"
              icon
              small
              :loading="refreshing"
              color="white"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-system-bar>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" opacity="0.8" z-index="9999">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 white--text">{{ loadingMessage }}</div>
      </div>
    </v-overlay>

    <!-- Error Alert -->
    <v-alert
      v-if="error"
      type="error"
      dismissible
      @input="error = null"
      class="ma-4 noprint"
    >
      {{ error }}
    </v-alert>

    <!-- Report Content -->
    <div id="exportContent" class="export-content" :style="pageOrientation">
      <div class="page">
        <!-- Report Header -->
        <div class="report-header text-center mb-6">
          <div class="logo-container mb-4">
            <v-img
              :src="logoUrl"
              max-height="80"
              max-width="60"
              contain
              class="mx-auto"
              @error="onLogoError"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </div>

          <h1 class="head mb-2">
            เกณฑ์อัตรากำลังครูต่อจำนวนนักเรียน ประจำปี {{ displayYear }}
          </h1>
          <h2 class="head mb-2">
            {{ collegeInfo.college_name || "กำลังโหลด..." }}
          </h2>
          <div class="divider-line">___________________________</div>

          <!-- Additional Info (Print Hidden) -->
          <v-row class="mt-4 noprint" v-if="!loading">
            <v-col cols="12" md="4">
              <v-card outlined class="text-center pa-3">
                <v-icon color="primary" size="32" class="mb-2"
                  >mdi-school</v-icon
                >
                <div class="text-h6">{{ collegeInfo.college_name || "-" }}</div>
                <div class="text-caption">ชื่อสถานศึกษา</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined class="text-center pa-3">
                <v-icon color="success" size="32" class="mb-2"
                  >mdi-calendar</v-icon
                >
                <div class="text-h6">{{ displayYear }}</div>
                <div class="text-caption">ปีการศึกษา</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined class="text-center pa-3">
                <v-icon color="info" size="32" class="mb-2"
                  >mdi-account-group</v-icon
                >
                <div class="text-h6">{{ totalStudents.toLocaleString() }}</div>
                <div class="text-caption">จำนวนนักเรียนรวม</div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Main Data Table -->
        <v-card outlined elevation="2" class="mb-6">
          <v-card-text class="pa-0">
            <div class="table-wrapper">
              <v-simple-table class="report-table" :loading="loading" dense>
                <template v-slot:loading>
                  <div class="text-center pa-4">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                    <div class="mt-2">กำลังโหลดข้อมูล...</div>
                  </div>
                </template>

                <thead>
                  <tr class="table-header">
                    <th class="text-center header-cell" width="5%">
                      <span class="bold16">ลำดับ</span>
                    </th>
                    <th class="text-center header-cell" width="20%">
                      <span class="bold16">ประเภท</span>
                    </th>
                    <th class="text-center header-cell" width="10%">
                      <span class="bold16">หลักสูตร</span>
                    </th>
                    <th class="text-center header-cell" width="30%">
                      <span class="bold16">สาขาวิชา</span>
                    </th>
                    <th class="text-center header-cell" width="15%">
                      <span class="bold16">จำนวนนักเรียน</span>
                    </th>
                    <th class="text-center header-cell" width="20%">
                      <span class="bold16">อัตรากำลัง/สาขา</span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <!-- General Department Section -->
                  <tr class="department-header">
                    <td colspan="4" class="text-center department-cell">
                      <span class="regular16 font-weight-bold">
                        <v-icon left small>mdi-bookmark</v-icon>
                        แผนกวิชาสามัญสัมพันธ์
                      </span>
                    </td>
                    <td class="text-center department-cell">
                      <v-chip color="primary" dark class="font-weight-bold">
                        {{ formatNumber(generalDepartmentStudents) }}
                      </v-chip>
                    </td>
                    <td class="text-center department-cell">
                      <v-chip color="success" dark class="font-weight-bold">
                        {{ formatDecimal(generalDepartmentManpower) }}
                      </v-chip>
                    </td>
                  </tr>

                  <!-- No Data Message -->
                  <tr v-if="!loading && branchData.length === 0">
                    <td colspan="6" class="text-center pa-4">
                      <v-icon size="48" color="grey lighten-1" class="mb-2">
                        mdi-database-off
                      </v-icon>
                      <div class="text-body-1 grey--text">
                        ไม่มีข้อมูลสาขาวิชา
                      </div>
                    </td>
                  </tr>

                  <!-- Branch Data Rows -->
                  <tr
                    v-for="(item, index) in branchData"
                    :key="item.id || index"
                    class="data-row"
                    :class="{ 'row-highlight': index % 2 === 0 }"
                  >
                    <td class="text-center data-cell">
                      <span class="regular16 font-weight-medium">{{
                        index + 1
                      }}</span>
                    </td>

                    <td class="text-center data-cell">
                      <v-chip
                        small
                        :color="getCourseTypeColor(item.type_course)"
                        dark
                        class="font-weight-medium"
                      >
                        {{ item.type_course || "-" }}
                      </v-chip>
                    </td>

                    <td class="text-center data-cell">
                      <span class="regular16">{{
                        item.year_course || "-"
                      }}</span>
                    </td>

                    <td class="branch-name data-cell">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="getBranchIconColor(item.type_course)"
                          left
                          small
                        >
                          {{ getBranchIcon(item.type_course) }}
                        </v-icon>
                        <span class="regular16">{{
                          item.branch_name_th || "-"
                        }}</span>
                      </div>
                    </td>

                    <td class="text-center data-cell">
                      <span
                        class="regular16 font-weight-medium"
                        :class="getStudentCountClass(item.sumStudent)"
                      >
                        {{ formatNumber(item.sumStudent) }}
                      </span>
                    </td>

                    <td class="text-center data-cell">
                      <v-chip
                        small
                        :color="getManpowerColor(calculateBranchManpower(item))"
                        dark
                        class="font-weight-bold"
                      >
                        {{ formatDecimal(calculateBranchManpower(item)) }}
                      </v-chip>
                    </td>
                  </tr>

                  <!-- Summary Row -->
                  <tr class="summary-row" v-if="branchData.length > 0">
                    <td colspan="4" class="text-center summary-cell">
                      <span class="regular16 font-weight-bold">
                        <v-icon left small>mdi-calculator</v-icon>
                        รวมทั้งหมด
                      </span>
                    </td>
                    <td class="text-center summary-cell">
                      <v-chip color="primary" dark class="font-weight-bold">
                        {{ formatNumber(totalStudents) }}
                      </v-chip>
                    </td>
                    <td class="text-center summary-cell">
                      <v-chip color="success" dark class="font-weight-bold">
                        {{ formatDecimal(totalManpower) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>

        <!-- Statistics Summary (Print Hidden) -->
        <v-row class="mb-6 noprint" v-if="!loading && branchData.length > 0">
          <v-col
            cols="12"
            md="3"
            v-for="stat in statisticsData"
            :key="stat.title"
          >
            <v-card outlined class="text-center pa-3">
              <v-icon :color="stat.color" size="28" class="mb-2">
                {{ stat.icon }}
              </v-icon>
              <div
                class="text-h6 font-weight-bold"
                :class="`${stat.color}--text`"
              >
                {{ stat.value }}
              </div>
              <div class="text-caption">{{ stat.title }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Signature Section -->
        <div class="signature-section mt-8">
          <v-row>
            <v-col cols="12" md="8"></v-col>
            <v-col cols="12" md="4">
              <div class="signature-box text-center">
                <div class="signature-line mb-3">
                  <span class="regular16">
                    ลงชื่อ.................................................................
                  </span>
                </div>
                <div class="signature-name mb-3">
                  <span class="regular16">
                    (.................................................)
                  </span>
                </div>
                <div class="signature-title">
                  <span class="regular16 font-weight-medium">
                    ผู้อำนวยการ{{ collegeInfo.college_name || "" }}
                  </span>
                </div>

                <!-- Date and Print Info -->
                <div class="mt-4 text-caption grey--text noprint">
                  <div>วันที่พิมพ์: {{ currentDate }}</div>
                  <div>พิมพ์โดย: {{ currentUser }}</div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Report Footer -->
        <div class="report-footer mt-6">
          <v-divider class="mb-3"></v-divider>
          <v-row>
            <v-col cols="12" md="6">
              <div class="text-caption">
                <strong>หมายเหตุ:</strong>
                <ul class="mt-1">
                  <li>
                    อัตรากำลัง/สาขา คำนวณจากสัดส่วนจำนวนนักเรียนในแต่ละสาขา
                  </li>
                  <li>แผนกวิชาสามัญสัมพันธ์ รวมนักเรียน ปวช. และ ปวส.</li>
                  <li>ข้อมูล ณ วันที่ {{ currentDate }}</li>
                </ul>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="text-caption">
                <div>รหัสเอกสาร: {{ documentId }}</div>
                <div>หน้า: <span id="pageNumber">1</span></div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
export default {
  name: "ImprovedTeacherRatioReport",

  data() {
    return {
      // API Configuration
      ApiKey: "HRvec2021",

      // Loading States
      loading: false,
      refreshing: false,
      printing: false,
      exporting: false,
      exportingPDF: false,
      exportingExcel: false,
      loadingMessage: "กำลังโหลดข้อมูล...",

      // Data
      collegeInfo: {},
      branchData: [],

      // Error Handling
      error: null,
      logoError: false,

      // Notifications
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Page Settings
      pageOrientation: "mso-page-orientation: landscape;"
    };
  },

  computed: {
    // URL Parameters
    years() {
      return this.$route.params.rate_work_g_year || new Date().getFullYear() + 543;
    },

    collegeCode() {
      return this.$route.params.college_code || "";
    },

    displayYear() {
      return this.years;
    },

    // Data Status
    hasData() {
      return (
        this.branchData.length > 0 || Object.keys(this.collegeInfo).length > 0
      );
    },

    statusChipColor() {
      if (this.loading) return "warning";
      return this.hasData ? "success" : "error";
    },

    statusIcon() {
      if (this.loading) return "mdi-loading";
      return this.hasData ? "mdi-check" : "mdi-alert";
    },

    statusText() {
      if (this.loading) return "กำลังโหลด...";
      if (this.hasData) return `ข้อมูล ${this.branchData.length} สาขา`;
      return "ไม่มีข้อมูล";
    },

    // Logo URL
    logoUrl() {
      return this.logoError ? "/placeholder-logo.png" : "/ovec.png";
    },

    // Date and User Info
    currentDate() {
      return new Date().toLocaleDateString("th-TH", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },

    currentUser() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.user_firstname || "ระบบ";
    },

    documentId() {
      return `RPT-${this.collegeCode}-${this.years}-${Date.now()
        .toString()
        .slice(-6)}`;
    },

    // Calculations
    generalDepartmentStudents() {
      const stdVc = parseInt(this.collegeInfo.std_vc) || 0;
      const stdHvc = parseInt(this.collegeInfo.std_hvc) || 0;
      return stdVc + stdHvc;
    },

    generalDepartmentManpower() {
      const official =
        parseFloat(this.collegeInfo.rate_work_g_gs_ta_official) || 0;
      const civil = parseFloat(this.collegeInfo.rate_work_g_gs_tb_civil) || 0;
      return official + civil;
    },

    totalStudents() {
      const generalStudents = this.generalDepartmentStudents;
      const branchStudents = this.branchData.reduce((sum, item) => {
        return sum + (parseInt(item.sumStudent) || 0);
      }, 0);
      return generalStudents + branchStudents;
    },

    totalManpower() {
      const generalManpower = this.generalDepartmentManpower;
      const branchManpower = this.branchData.reduce((sum, item) => {
        return sum + this.calculateBranchManpower(item);
      }, 0);
      return generalManpower + branchManpower;
    },

    statisticsData() {
      const avgStudentsPerBranch =
        this.branchData.length > 0
          ? Math.round(this.totalStudents / this.branchData.length)
          : 0;

      const teacherStudentRatio =
        this.totalManpower > 0
          ? Math.round(this.totalStudents / this.totalManpower)
          : 0;

      return [
        {
          title: "จำนวนสาขาวิชา",
          value: this.branchData.length.toString(),
          icon: "mdi-school-outline",
          color: "primary"
        },
        {
          title: "เฉลี่ยนักเรียน/สาขา",
          value: avgStudentsPerBranch.toLocaleString(),
          icon: "mdi-calculator",
          color: "info"
        },
        {
          title: "อัตราส่วนครู:นักเรียน",
          value: `1:${teacherStudentRatio}`,
          icon: "mdi-account-group",
          color: "success"
        },
        {
          title: "อัตรากำลังรวม",
          value: this.formatDecimal(this.totalManpower),
          icon: "mdi-account-tie",
          color: "warning"
        }
      ];
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    // Initialization
    async initializeComponent() {
      this.loading = true;
      this.loadingMessage = "กำลังโหลดข้อมูลรายงาน...";

      try {
        await Promise.allSettled([
          this.loadCollegeData(),
          this.loadBranchData()
        ]);

        if (this.hasData) {
          this.showSuccessMessage("โหลดข้อมูลรายงานเรียบร้อยแล้ว");
        } else {
          this.showWarningMessage("ไม่พบข้อมูลสำหรับปีการศึกษาที่เลือก");
        }
      } catch (error) {
        this.handleError("ไม่สามารถโหลดข้อมูลรายงานได้", error);
      } finally {
        this.loading = false;
      }
    },

    // Data Loading
    async loadCollegeData() {
      try {
        const result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_college_code: this.collegeCode,
          rate_work_g_year: this.years
        });

        this.collegeInfo = result.data || {};
      } catch (error) {
        console.error("Error loading college data:", error);
        throw error;
      }
    },

    async loadBranchData() {
      try {
        const result = await this.$http.post(
          "rate_work_college_cal_idbranch.php",
          {
            ApiKey: this.ApiKey,
            rate_work_college_code: this.collegeCode,
            rate_work_college_year: this.years
          }
        );

        this.branchData = Array.isArray(result.data) ? result.data : [];
      } catch (error) {
        console.error("Error loading branch data:", error);
        throw error;
      }
    },

    async refreshData() {
      this.refreshing = true;
      try {
        await this.initializeComponent();
        this.showSuccessMessage("รีเฟรชข้อมูลเรียบร้อยแล้ว");
      } catch (error) {
        this.handleError("ไม่สามารถรีเฟรชข้อมูลได้", error);
      } finally {
        this.refreshing = false;
      }
    },

    // Calculations
    calculateBranchManpower(item) {
      if (!item || !item.sumStudent || !item.sumAllStd || !item.manPower) {
        return 0;
      }

      const percentage =
        (parseFloat(item.sumStudent) * 100) / parseFloat(item.sumAllStd);
      const manpower = (percentage * parseFloat(item.manPower)) / 100;
      return manpower;
    },

    // Formatting Functions
    formatNumber(value) {
      const num = parseInt(value) || 0;
      return num === 0 ? "-" : num.toLocaleString();
    },

    formatDecimal(value, decimals = 2) {
      const num = parseFloat(value) || 0;
      return num === 0 ? "-" : num.toFixed(decimals);
    },

    // Styling Functions
    getCourseTypeColor(type) {
      const colors = {
        "ปวช.": "blue",
        "ปวส.": "green",
        ปริญญา: "purple",
        ระยะสั้น: "orange"
      };
      return colors[type] || "grey";
    },

    getBranchIcon(type) {
      const icons = {
        "ปวช.": "mdi-school",
        "ปวส.": "mdi-school-outline",
        ปริญญา: "mdi-account-school",
        ระยะสั้น: "mdi-clock-fast"
      };
      return icons[type] || "mdi-bookmark";
    },

    getBranchIconColor(type) {
      return this.getCourseTypeColor(type);
    },

    getStudentCountClass(count) {
      const num = parseInt(count) || 0;
      if (num === 0) return "grey--text";
      if (num > 100) return "green--text text--darken-2";
      if (num > 50) return "blue--text text--darken-2";
      return "";
    },

    getManpowerColor(manpower) {
      const value = parseFloat(manpower) || 0;
      if (value === 0) return "grey";
      if (value >= 5) return "success";
      if (value >= 2) return "warning";
      return "error";
    },

    // Export Functions
    async handlePrint() {
      this.printing = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 100));
        window.print();
        this.showSuccessMessage("เริ่มการพิมพ์แล้ว");
      } catch (error) {
        this.handleError("เกิดข้อผิดพลาดในการพิมพ์", error);
      } finally {
        this.printing = false;
      }
    },

    async exportToWord() {
      this.exporting = true;
      try {
        const element = document.getElementById("exportContent");
        const preHtml = `
          <html xmlns:o='urn:schemas-microsoft-com:office:office' 
                xmlns:w='urn:schemas-microsoft-com:office:word' 
                xmlns='http://www.w3.org/TR/REC-html40'>
          <head>
            <meta charset='utf-8'>
            <title>เกณฑ์อัตรากำลังครู - ${this.collegeInfo.college_name}</title>
            <style>
              body { font-family: 'TH SarabunIT๙', 'TH SarabunPSK'; font-size: 16pt; }
              table { border-collapse: collapse; width: 100%; }
              td, th { border: 1px solid black; padding: 8px; text-align: center; }
              .header-cell { background-color: #e8f5e8; font-weight: bold; }
              .bold16 { font-weight: bold; font-size: 16pt; }
              .regular16 { font-size: 16pt; }
            </style>
          </head>
          <body>
        `;
        const postHtml = "</body></html>";

        const cleanContent = element.innerHTML
          .replace(/class="[^"]*"/g, "")
          .replace(/<v-[^>]*>/g, "")
          .replace(/<\/v-[^>]*>/g, "")
          .replace(/noprint[^>]*>/g, 'style="display:none">');

        const html = preHtml + cleanContent + postHtml;
        const blob = new Blob(["\ufeff", html], { type: "application/msword" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `เกณฑ์อัตรากำลังครู_${this.collegeCode}_${this.years}.doc`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.showSuccessMessage("ส่งออกไฟล์ Word เรียบร้อยแล้ว");
      } catch (error) {
        this.handleError("เกิดข้อผิดพลาดในการส่งออกไฟล์ Word", error);
      } finally {
        this.exporting = false;
      }
    },

    async exportToPDF() {
      this.exportingPDF = true;
      try {
        const originalTitle = document.title;
        document.title = `เกณฑ์อัตรากำลังครู_${this.collegeCode}_${this.years}`;

        const printCSS = `
          <style media="print">
            @page { 
              size: A4 landscape; 
              margin: 1cm; 
            }
            .noprint { display: none !important; }
            body { font-size: 12pt; }
            table { font-size: 11pt; }
            .page { border: none; box-shadow: none; }
          </style>
        `;

        document.head.insertAdjacentHTML("beforeend", printCSS);
        window.print();

        setTimeout(() => {
          document.title = originalTitle;
        }, 1000);

        this.showSuccessMessage("เปิดหน้าต่างการพิมพ์เป็น PDF แล้ว");
      } catch (error) {
        this.handleError("เกิดข้อผิดพลาดในการส่งออกไฟล์ PDF", error);
      } finally {
        this.exportingPDF = false;
      }
    },

    async exportToExcel() {
      this.exportingExcel = true;
      try {
        let csvContent =
          "ลำดับ,ประเภท,หลักสูตร,สาขาวิชา,จำนวนนักเรียน,อัตรากำลัง/สาขา\n";

        // Add general department row
        csvContent += `,"แผนกวิชาสามัญสัมพันธ์",,,"${
          this.generalDepartmentStudents
        }","${this.formatDecimal(this.generalDepartmentManpower)}"\n`;

        // Add branch data
        this.branchData.forEach((item, index) => {
          const row = [
            index + 1,
            `"${item.type_course || ""}"`,
            `"${item.year_course || ""}"`,
            `"${item.branch_name_th || ""}"`,
            parseInt(item.sumStudent) || 0,
            this.formatDecimal(this.calculateBranchManpower(item))
          ].join(",");
          csvContent += row + "\n";
        });

        // Add summary row
        csvContent += `"รวมทั้งหมด",,,"","${
          this.totalStudents
        }","${this.formatDecimal(this.totalManpower)}"\n`;

        const blob = new Blob(["\ufeff" + csvContent], {
          type: "text/csv;charset=utf-8;"
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `เกณฑ์อัตรากำลังครู_${this.collegeCode}_${this.years}.csv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.showSuccessMessage("ส่งออกไฟล์ Excel เรียบร้อยแล้ว");
      } catch (error) {
        this.handleError("เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel", error);
      } finally {
        this.exportingExcel = false;
      }
    },

    // Event Handlers
    onLogoError() {
      this.logoError = true;
    },

    // Notification Methods
    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        color: "success",
        text: message,
        icon: "mdi-check-circle",
        timeout: 5000
      };
    },

    showWarningMessage(message) {
      this.snackbar = {
        show: true,
        color: "warning",
        text: message,
        icon: "mdi-alert",
        timeout: 6000
      };
    },

    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        color: "error",
        text: message,
        icon: "mdi-alert-circle",
        timeout: 8000
      };
    },

    // Error Handling
    handleError(message, error = null) {
      console.error("Report Error:", error);
      this.error = message;
      this.showErrorMessage(message);
    }
  },

  // Cleanup
  beforeDestroy() {
    // Clean up any resources if needed
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@100;300;400;500;600;700&display=swap");

/* Global Font Settings */
* {
  font-family: "TH SarabunIT๙", "TH SarabunPSK", "Sarabun", sans-serif;
  box-sizing: border-box;
}

body {
  font-family: "TH SarabunIT๙", "TH SarabunPSK", "Sarabun", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

/* Page Layout */
.export-content {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 20px 0;
}

.page {
  width: 100%;
  max-width: 1400px;
  min-height: 21cm;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  margin: 0 auto;
  padding: 2cm;
  position: relative;
}

/* Typography */
.head {
  font-size: 20pt;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 12px;
  line-height: 1.3;
}

.regular12 {
  font-size: 14pt;
  font-weight: 400;
}

.regular16 {
  font-size: 16pt;
  font-weight: 400;
}

.bold16 {
  font-size: 16pt;
  font-weight: 600;
}

.blod12 {
  font-size: 14pt;
  font-weight: 600;
}

.blackRegula10 {
  font-size: 12pt;
  font-weight: 400;
}

.blackBold18 {
  font-size: 18pt;
  font-weight: 700;
}

/* Report Header */
.report-header {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.divider-line {
  font-size: 18pt;
  color: #1976d2;
  margin-top: 16px;
}

/* Table Styles */
.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.report-table >>> table {
  border-radius: 12px;
}

/* Table Headers */
.table-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.header-cell {
  background: #1976d2 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 16px 12px !important;
  border: 1px solid #1565c0 !important;
  text-align: center !important;
  vertical-align: middle !important;
}

/* Department Header */
.department-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
}

.department-cell {
  background: #4caf50 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 14px 12px !important;
  border: 1px solid #388e3c !important;
  text-align: center !important;
}

/* Data Rows */
.data-row {
  transition: all 0.2s ease;
}

.data-row:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
  transform: translateY(-1px);
}

.row-highlight {
  background-color: rgba(0, 0, 0, 0.02);
}

.data-cell {
  padding: 12px !important;
  border: 1px solid #e0e0e0 !important;
  vertical-align: middle !important;
}

.branch-name {
  text-align: left !important;
  min-width: 200px;
}

/* Summary Row */
.summary-row {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.summary-cell {
  background: #ff9800 !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 16px 12px !important;
  border: 1px solid #f57c00 !important;
  text-align: center !important;
}

/* Signature Section */
.signature-section {
  margin-top: 3rem;
  padding-top: 2rem;
}

.signature-box {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 2rem;
  background: #fafafa;
}

.signature-line {
  border-bottom: 1px dotted #333;
  padding-bottom: 4px;
  margin-bottom: 1rem;
}

/* Report Footer */
.report-footer {
  margin-top: 2rem;
  padding-top: 1rem;
  font-size: 12pt;
}

.report-footer ul {
  padding-left: 20px;
  margin: 0;
}

.report-footer li {
  margin-bottom: 6px;
}

/* Cards and UI Elements */
.v-card {
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.v-chip {
  font-weight: 500 !important;
  border-radius: 20px !important;
}

/* Buttons */
.v-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.v-btn:hover {
  transform: translateY(-1px);
}

/* System Bar */
.v-system-bar {
  border-radius: 0 0 12px 12px !important;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .page {
    margin: 0 16px;
    padding: 1.5cm;
  }
}

@media (max-width: 768px) {
  .page {
    margin: 0 8px;
    padding: 1cm;
  }

  .head {
    font-size: 18pt;
  }

  .bold16,
  .regular16 {
    font-size: 14pt;
  }

  .data-cell {
    padding: 8px 4px !important;
    font-size: 12pt;
  }

  .header-cell {
    padding: 12px 8px !important;
    font-size: 12pt;
  }

  .signature-box {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .branch-name {
    min-width: 150px;
  }

  .head {
    font-size: 16pt;
  }

  .table-wrapper {
    margin: 0 -8px;
  }
}

/* Print Styles */
@media print {
  body {
    background: white !important;
    color: black !important;
    font-size: 12pt !important;
  }

  .export-content {
    background: white !important;
    padding: 0 !important;
  }

  .page {
    border: none !important;
    border-radius: 0 !important;
    width: 100% !important;
    max-width: none !important;
    min-height: initial !important;
    box-shadow: none !important;
    background: white !important;
    margin: 0 !important;
    padding: 1cm !important;
    page-break-after: always;
  }

  .page:last-child {
    page-break-after: auto;
  }

  .noprint {
    display: none !important;
  }

  .head {
    font-size: 16pt !important;
    color: black !important;
  }

  .regular12 {
    font-size: 12pt !important;
  }

  .regular16 {
    font-size: 12pt !important;
  }

  .bold16 {
    font-size: 12pt !important;
    font-weight: bold !important;
  }

  .blod12 {
    font-size: 12pt !important;
    font-weight: bold !important;
  }

  .blackRegula10 {
    font-size: 10pt !important;
  }

  .blackBold18 {
    font-size: 14pt !important;
    font-weight: bold !important;
  }

  /* Print Table Styles */
  .report-table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  .report-table td,
  .report-table th {
    border: 1px solid black !important;
    padding: 6px 4px !important;
    font-size: 10pt !important;
  }

  .header-cell {
    background: #d0d0d0 !important;
    color: black !important;
    font-weight: bold !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .department-cell {
    background: #e8e8e8 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .summary-cell {
    background: #f0f0f0 !important;
    color: black !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .signature-box {
    border: 1px solid black !important;
    background: white !important;
    padding: 1rem !important;
  }

  /* Print Page Breaks */
  .data-row {
    page-break-inside: avoid;
  }

  .summary-row {
    page-break-before: avoid;
  }

  .signature-section {
    page-break-before: avoid;
  }

  /* Print Color Adjustments */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* Animation and Transitions */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter,
.v-leave-to {
  opacity: 0;
}

/* Loading States */
.v-skeleton-loader {
  border-radius: 8px;
}

/* Error States */
.error-state {
  opacity: 0.6;
  pointer-events: none;
}

/* Success States */
.success-highlight {
  background-color: rgba(76, 175, 80, 0.1);
  border-left: 4px solid #4caf50;
}

/* Accessibility */
.v-btn:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.report-table:focus-within {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .header-cell {
    background: #000000 !important;
    color: #ffffff !important;
    border: 2px solid #ffffff !important;
  }

  .data-cell {
    border: 1px solid #000000 !important;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .v-application:not(.theme--light) .page {
    background: #1e1e1e;
    color: #ffffff;
  }

  .v-application:not(.theme--light) .report-table {
    background: #2d2d2d;
  }

  .v-application:not(.theme--light) .data-cell {
    border-color: #404040 !important;
  }
}

/* Custom Scrollbar */
.table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Utility Classes */
.text-nowrap {
  white-space: nowrap;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.border-radius-lg {
  border-radius: 12px !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

.shadow-md {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

.shadow-lg {
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1) !important;
}

/* Special Effects */
.gradient-text {
  background: linear-gradient(45deg, #1976d2, #42a5f5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-effect {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Table Enhancement */
.report-table >>> thead th {
  position: sticky;
  top: 0;
  z-index: 10;
}

.report-table >>> tbody tr:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.02);
}

.report-table >>> tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

/* Enhanced Signature Box */
.signature-box {
  position: relative;
  overflow: hidden;
}

.signature-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
}

/* Status Indicators */
.status-online {
  position: relative;
}

.status-online::after {
  content: "";
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  border: 2px solid white;
}
</style>
